import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Icon } from "@iconify/react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { format } from "date-fns";
import "../styles/general.css";
import "../styles/main.css";
import "../styles/userlist.css";
import "../styles/OpenCall.css";
import '../styles/DropdownMenu.css';
import { URL } from "../core/config";
import openCallService from "../core/services/OpenCallService";
import eventService from "../core/services/EventService";
import CustomAlert from './CustomAlert';
import CustomAlertWithButtons from './CustomAlertWithButtons';
import OpenCallTieBreaker from './OpenCallTieBreaker';
import LoadingSpinner from './LoadingSpinner';
import Avatar from './Avatar';

import { useAuth } from "../core/AuthContext";
import { useSemester } from "../core/SemesterContext";
import NavHeader from "./NavHeader";
import {
  canVoteOpenCall,
  canViewOpenCallResults,
} from "../core/utils/permissionUtils";
import { departmentMatchesFilter } from "../core/constants/departments";
import {
  buildEventsByOpenCallId,
  buildEventsByOpenCallIdFromList,
  categorizeActiveOpenCalls,
  collectOpenCallUserIds,
  getOpenCallVoteCount,
  getOpenCallTieGroups,
  getShotCountClass,
  normalizeOpenCallId,
  openCallUserHasVoted,
  resolveOpenCallWinners,
} from "../core/utils/openCallUtils";

const opencallURL = URL + "/opencall";
const usersURL = URL + "/users";
const CLOSED_PREVIEW_COUNT = 3;

function OpenCallList({
  embedded = false,
  departmentFilter = null,
  initialOpenCalls = null,
  initialEvents = null,
  initialUsers = null,
  skipInitialFetch = false,
}) {
  const authContext = useAuth();
  const contextUser = authContext.user;
  const location = useLocation();
  const history = useHistory();
  const openCallsListPath = `${location.pathname}${location.search}`;

  const semesterContext = useSemester();
  const semester = semesterContext.currentSemester;

  const [events, setEvents] = useState({});

  const [ongoingOpencalls, setOngoingOpencalls] = useState([]);
  const [votingOpencalls, setVotingOpencalls] = useState([]);
  const [, setCanceledOpencalls] = useState([]); // For now are being saved but not retrieved

  const [usersById, setUsersById] = useState({});
  const [closedPage, setClosedPage] = useState(1);
  const [closedTotalPages, setClosedTotalPages] = useState(0);
  const [closedPageItems, setClosedPageItems] = useState([]);
  const [closedPreviewExpanded, setClosedPreviewExpanded] = useState(false);
  const [closedSectionLoading, setClosedSectionLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const [showCommentInputFor, setShowCommentInputFor] = useState(null); // openCallId or null
  const [shotComment, setShotComment] = useState("");

  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  const sendEmailNotificationRef = useRef(sendEmailNotification);
  const semesterIdRef = useRef(null);
  const closedPreviewExpandedRef = useRef(false);
  const [isMounted, setIsMounted] = useState(true);
  const [dropdownVisibility, setDropdownVisibility] = useState({});

  // For CustomAlert
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: "",
    message: "",
  });

  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: "",
    type: "",
    message: "",
    btn1Action: () => {},
    btn2Action: () => {},
    btn1Text: "",
    btn2Text: "",
  });

  const [tieBreakerInfo, setTieBreakerInfo] = useState({
    show: false,
    openCallId: null,
    eventId: null,
    votes: null,
    spots: 0,
    automaticWinners: [],
    tieGroups: [],
    selections: {},
    validationError: "",
  });

  semesterIdRef.current = semester?._id;
  closedPreviewExpandedRef.current = closedPreviewExpanded;

  const filterOpenCallsForDepartment = useCallback(
    (openCalls, eventMap) => {
      if (!departmentFilter) return openCalls;
      return openCalls.filter((opencall) => {
        const eventData = eventMap[normalizeOpenCallId(opencall._id)];
        if (!eventData) return false;
        return eventData.departments?.some((dep) =>
          departmentMatchesFilter(dep, departmentFilter),
        );
      });
    },
    [departmentFilter],
  );

  const updatePastDisplay = useCallback((items, { expand = false } = {}) => {
    setClosedPageItems(items || []);
    setClosedPreviewExpanded(expand);
  }, []);

  const applyActiveOpenCalls = useCallback(
    (openCalls, eventMap) => {
      const filteredOpenCalls = filterOpenCallsForDepartment(openCalls, eventMap);
      const { ongoing, voting, canceled } = categorizeActiveOpenCalls(
        filteredOpenCalls,
        eventMap,
      );

      setEvents((prev) => ({ ...prev, ...eventMap }));
      setVotingOpencalls(voting);
      setOngoingOpencalls(ongoing);
      setCanceledOpencalls(canceled);
    },
    [filterOpenCallsForDepartment],
  );

  const applyOpenCallListData = useCallback(
    (openCalls, eventMap) => {
      applyActiveOpenCalls(openCalls, eventMap);
    },
    [applyActiveOpenCalls],
  );

  const mergeUsersIntoLookup = useCallback((userRows) => {
    if (!Array.isArray(userRows) || userRows.length === 0) return;
    setUsersById((prev) => {
      const next = { ...prev };
      userRows.forEach((user) => {
        if (user?._id) {
          next[String(user._id)] = user;
        }
      });
      return next;
    });
  }, []);

  const fetchUsersByIds = useCallback(
    async (ids) => {
      const uniqueIds = [...new Set((ids || []).map((id) => String(id)).filter(Boolean))];
      if (uniqueIds.length === 0) return;

      const response = await axios.get(`${usersURL}/by-ids`, {
        params: { ids: uniqueIds.join(",") },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      mergeUsersIntoLookup(response.data || []);
    },
    [mergeUsersIntoLookup],
  );

  const syncUsersForOpenCalls = useCallback(
    async (openCalls) => {
      const includeVoters = canViewOpenCallResults(contextUser);
      const ids = collectOpenCallUserIds(openCalls, { includeVoters });
      try {
        await fetchUsersByIds(ids);
      } catch (userError) {
        console.error("Error loading open call users:", userError);
      }
    },
    [contextUser, fetchUsersByIds],
  );

  const applyPrefetchedData = useCallback(() => {
    if (!initialOpenCalls || !initialEvents) return false;

    const eventMap = buildEventsByOpenCallId(initialOpenCalls, initialEvents);
    applyOpenCallListData(initialOpenCalls, eventMap);
    return true;
  }, [initialOpenCalls, initialEvents, applyOpenCallListData]);

  const fetchClosedOpenCallsPage = useCallback(
    async (page, semesterId = semesterIdRef.current, { expand } = {}) => {
      if (!semesterId) return [];

      const closedData = await openCallService.listClosed(semesterId, page, {
        department: departmentFilter || undefined,
      });
      const items = closedData?.items || [];
      const resolvedPage = closedData?.page || page;

      setClosedTotalPages(closedData?.totalPages || 0);
      setClosedPage(resolvedPage);
      updatePastDisplay(items, {
        expand:
          expand ??
          (resolvedPage !== 1 || closedPreviewExpandedRef.current),
      });

      const closedEventMap = buildEventsByOpenCallIdFromList(items);
      setEvents((prev) => ({ ...prev, ...closedEventMap }));

      return items;
    },
    [departmentFilter, updatePastDisplay],
  );

  const fetchActiveOpenCalls = useCallback(
    async (semesterId = semesterIdRef.current) => {
      if (!semesterId) return [];

      const activeOpenCalls = await openCallService.listActive(semesterId, {
        department: departmentFilter || undefined,
      });
      const eventMap = buildEventsByOpenCallIdFromList(activeOpenCalls);
      applyActiveOpenCalls(activeOpenCalls, eventMap);
      return activeOpenCalls;
    },
    [applyActiveOpenCalls, departmentFilter],
  );

  const fetchOpencalls = async (semesterId = semesterIdRef.current) => {
    if (!semesterId) return;

    try {
      const activeOpenCalls = await fetchActiveOpenCalls(semesterId);
      await syncUsersForOpenCalls(activeOpenCalls);
    } catch (error) {
      console.error("Error fetching opencalls:", error);
      throw error;
    }
  };

  const refreshOpenCallLists = async () => {
    const semesterId = semesterIdRef.current;
    if (!semesterId) return;

    try {
      const [activeOpenCalls, closedItems] = await Promise.all([
        fetchActiveOpenCalls(semesterId),
        fetchClosedOpenCallsPage(closedPage, semesterId),
      ]);
      await syncUsersForOpenCalls([...(activeOpenCalls || []), ...(closedItems || [])]);
    } catch (refreshError) {
      console.error("Error refreshing open calls:", refreshError);
      throw refreshError;
    }
  };

  const fetchOpencallsAndUsers = async () => {
    const semesterId = semesterIdRef.current;
    if (!semesterId) return;

    try {
      setLoading(true);

      if (embedded && skipInitialFetch && Array.isArray(initialUsers)) {
        mergeUsersIntoLookup(initialUsers);
      }

      const activePromise = openCallService.listActive(semesterId, {
        department: departmentFilter || undefined,
      });

      const closedPromise = openCallService.listClosed(semesterId, 1, {
        department: departmentFilter || undefined,
      });

      const [activeOpenCalls, closedData] = await Promise.all([
        activePromise,
        closedPromise,
      ]);

      const activeEventMap = buildEventsByOpenCallIdFromList(activeOpenCalls || []);
      applyActiveOpenCalls(activeOpenCalls || [], activeEventMap);

      let closedItems = [];
      if (closedData) {
        closedItems = closedData.items || [];
        setClosedTotalPages(closedData.totalPages || 0);
        setClosedPage(closedData.page || 1);
        updatePastDisplay(closedItems, { expand: false });

        const closedEventMap = buildEventsByOpenCallIdFromList(closedItems);
        setEvents((prev) => ({ ...prev, ...closedEventMap }));
      }

      await syncUsersForOpenCalls([
        ...(activeOpenCalls || []),
        ...closedItems,
      ]);
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(initialUsers) && initialUsers.length > 0) {
      mergeUsersIntoLookup(initialUsers);
    }
  }, [initialUsers, mergeUsersIntoLookup]);

  useEffect(() => {
    if (skipInitialFetch && applyPrefetchedData()) {
      setLoading(false);
      const semesterId = semesterIdRef.current;
      if (semesterId) {
        fetchClosedOpenCallsPage(1, semesterId)
          .then((items) => syncUsersForOpenCalls(items || []))
          .catch((fetchError) => {
            console.error("Error loading closed open calls:", fetchError);
          });
      }
      return;
    }

    fetchOpencallsAndUsers().catch((fetchError) => {
      console.error("Error loading open calls:", fetchError);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semester?._id, departmentFilter, skipInitialFetch]);

  useEffect(() => {
    if (!skipInitialFetch || !initialOpenCalls || !initialEvents) return;
    applyPrefetchedData();
  }, [skipInitialFetch, initialOpenCalls, initialEvents, applyPrefetchedData]);

  // Effect to handle refresh state after voting submission
  useEffect(() => {
    if (location.state && location.state.refresh) {
      fetchOpencallsAndUsers().catch((fetchError) => {
        console.error("Error refreshing open calls:", fetchError);
      });
      history.replace({
        pathname: location.pathname,
        search: location.search,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, semester?._id]);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    sendEmailNotificationRef.current = sendEmailNotification;
  }, [sendEmailNotification]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-container')) {
        setDropdownVisibility({});
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  if (loading && !embedded) {
    return (
      <div className="page-container">
        <LoadingSpinner message="Loading open calls" />
      </div>
    );
  }
  if (error && !embedded) return <div>Error: {error.message}</div>;

  const toggleEventInfo = (eventId) => {
    setSelectedEventId(selectedEventId === eventId ? null : eventId);
  };

  const getUserNameById = (userId) => {
    const user = usersById[String(userId)];
    if (user && user.nickname) {
      return `${user.nickname} (${user.firstLastName || user.fullName} )`;
    }
    return user ? user.nickname || user.fullName : "Unknown";
  };

  const getUserById = (id) => usersById[String(id)];

  const patchOpenCallInLists = (openCallId, patch) => {
    const applyPatch = (list) =>
      list.map((opencall) =>
        String(opencall._id) === String(openCallId) ? { ...opencall, ...patch } : opencall,
      );

    setOngoingOpencalls(applyPatch);
    setVotingOpencalls(applyPatch);
    setClosedPageItems(applyPatch);
  };

  const handleSendShot = async (openCallId, e, comment = "") => {
    e.stopPropagation();

    if (isUserSuspended(contextUser)) {
      setAlertInfo({
        show: true,
        type: "warning",
        message: getSuspensionShotMessage(contextUser),
      });
      return;
    }

    try {
      // Attempt to send the shot
      const result = await openCallService.sendShot(openCallId, contextUser._id, comment);

      if (result?.openCall) {
        patchOpenCallInLists(openCallId, { shots: result.openCall.shots });
        await fetchUsersByIds([contextUser._id]);
      }

      setAlertInfo({
        show: true,
        type: "success",
        message: "Your shot was recorded successfully!",
      });
      setShowCommentInputFor(null);
      setShotComment("");
    } catch (error) {
      // If an error occurs, show error message
      setAlertInfo({
        show: true,
        type: "error",
        message: "Failed to record the shot. Please try again.",
      });
      console.log(error.message);
    }
  };

  const handleCancelShot = async (openCallId, e) => {
    e.stopPropagation();

    try {
      // Attempt to cancel the shot
      const result = await openCallService.cancelShot(openCallId, contextUser._id);

      if (result?.openCall) {
        patchOpenCallInLists(openCallId, { shots: result.openCall.shots });
      }

      setAlertInfo({
        show: true,
        type: "success",
        message: "Your shot was cancelled successfully!",
      });
    } catch (error) {
      // If an error occurs, show error message
      setAlertInfo({
        show: true,
        type: "error",
        message: "Failed to cancel the shot. Please try again.",
      });
      console.log(error.message);
    }
  };

  const handleVoting = async (opencall, e) => {
    e.stopPropagation();
    history.push({
      pathname: `/opencalls/${opencall._id}/compare-sardines`,
      search: `?opencall=${opencall._id}`,
      state: { from: openCallsListPath },
    });
  };

  /*const handleCloseOpenCall = async (openCallId, eventId, winners, e) => {
    e.stopPropagation();
    setShowCloseConfirmation({ openCallId, eventId, winners });
  };*/

  const handleCloseOpenCall = async (openCallId, eventId, winners, e) => {
    if (e) e.stopPropagation();

    setAlertActionInfo({
      show: true,
      title: "Close Open Call Voting",
      type: "warning",
      message: (
        <>
          Are you sure you want to close this Open Call and announce the
          winners? <strong>This cannot be undone.</strong> <br />
          <br />
          <strong>Winners:</strong>{" "}
          {winners.length > 0
            ? winners.map((id) => getUserNameById(id)).join(", ")
            : "No winner for this open call"}
        </>
      ),
      btn1Action: () => setAlertActionInfo((prev) => ({ ...prev, show: false })),
      btn2Action: () => {
        setAlertActionInfo((prev) => ({ ...prev, show: false }));
        confirmCloseOpenCall(openCallId, eventId, winners);
      },
      btn1Text: "No, not yet",
      btn2Text: "Yes, close it",
    });
  };

  const initiateCloseOpenCall = (opencall, e) => {
    e.stopPropagation();

    const { automaticWinners, tieGroups } = getOpenCallTieGroups(
      opencall.votes,
      opencall.numberSpots,
    );

    if (tieGroups.length > 0) {
      setTieBreakerInfo({
        show: true,
        openCallId: opencall._id,
        eventId: opencall.event,
        votes: opencall.votes,
        spots: opencall.numberSpots,
        automaticWinners,
        tieGroups,
        selections: {},
        validationError: "",
        shots: opencall.shots || [],
      });
      return;
    }

    const winners = resolveOpenCallWinners(opencall.votes, opencall.numberSpots);
    handleCloseOpenCall(opencall._id, opencall.event, winners, e);
  };

  const handleTieBreakerSelectionChange = (groupIndex, selectedIds) => {
    setTieBreakerInfo((prev) => ({
      ...prev,
      selections: {
        ...prev.selections,
        [groupIndex]: selectedIds,
      },
      validationError: "",
    }));
  };

  const handleTieBreakerConfirm = () => {
    const { openCallId, eventId, votes, spots, tieGroups, selections } = tieBreakerInfo;

    const incompleteGroup = tieGroups.find((group, groupIndex) => {
      const selected = selections[groupIndex] || [];
      return selected.length !== group.spotsToFill;
    });

    if (incompleteGroup) {
      setTieBreakerInfo((prev) => ({
        ...prev,
        validationError: "Please select a winner for each tied spot before continuing.",
      }));
      return;
    }

    const winners = resolveOpenCallWinners(votes, spots, selections);

    setTieBreakerInfo({
      show: false,
      openCallId: null,
      eventId: null,
      votes: null,
      spots: 0,
      automaticWinners: [],
      tieGroups: [],
      selections: {},
      validationError: "",
      shots: [],
    });

    handleCloseOpenCall(openCallId, eventId, winners);
  };

  const getShotCommentByUserId = (userId) => {
    const shot = tieBreakerInfo.shots?.find(
      (entry) => String(entry.user) === String(userId),
    );
    return shot?.comment || "";
  };

  const confirmCloseOpenCall = async (openCallId, eventId, winners) => {
    try {
      await openCallService.saveWinners(openCallId, winners, true, true);
      await eventService.addParticipants(eventId, winners);

      setDropdownVisibility((prev) => {
        if (!prev[openCallId]) return prev;
        const next = { ...prev };
        delete next[openCallId];
        return next;
      });
      setSelectedEventId((prev) => (prev === openCallId ? null : prev));

      await refreshOpenCallLists();

      setAlertInfo({
        show: true,
        type: "success",
        message:
          "This Open Call was successfully closed and the winners were announced by email.",
      });
    } catch (error) {
      console.error("Failed to close open call:", error);
      try {
        await refreshOpenCallLists();
      } catch (refreshError) {
        console.error("Failed to refresh open calls after close error:", refreshError);
      }
      setAlertInfo({
        show: true,
        type: "error",
        message:
          "There was an error while closing this Open Call. Please try again later.",
      });
    }
  };

  const handleSeeVoting = async (opencall, e) => {
    e.stopPropagation();
    history.push({
      pathname: `/opencalls/${opencall._id}`,
      search: `?opencall=${opencall._id}`,
      state: { from: openCallsListPath },
    });
  };

  const handleReopenForVoting = async (opencall, e) => {
    e.stopPropagation();

    setAlertActionInfo({
      show: true,
      title: "Reopen Open Call for Voting",
      type: "warning",
      message: "Are you sure you want to reopen this Open Call for voting?",
      btn1Action: () => {
        setAlertActionInfo({ show: false });
      },
      btn2Action: async () => {
        // Get the current state value using a ref or functional update
        const currentValue = sendEmailNotificationRef.current;

        try {
          await axios.put(`${opencallURL}/${opencall._id}/reopen-voting`, {
            sendEmailNotification: currentValue,
          });

          if (isMounted) {
            const successMessage = currentValue
              ? "Open Call reopened for voting successfully! Email notification sent to Heads & Board."
              : "Open Call reopened for voting successfully!";

            setAlertInfo({
              show: true,
              type: "success",
              message: successMessage,
            });
          }

          await refreshOpenCallLists();
        } catch (error) {
          console.error(error);
          if (isMounted) {
            setAlertInfo({
              show: true,
              type: "error",
              message:
                "Failed to reopen the Open Call for voting. Please try again.",
            });
          }
        } finally {
          if (isMounted) {
            setAlertActionInfo({ show: false });
            setSendEmailNotification(true);
          }
        }
      },
      btn1Text: "No, cancel",
      btn2Text: "Yes, reopen it",
      // Add these new props to handle the checkbox
      showCheckbox: true,
      checkboxLabel: "Notify Heads & Board",
      checkboxValue: sendEmailNotification,
      onCheckboxChange: (value) => {
        setSendEmailNotification(value);
      },
    });
  };

  function formatDate(dateString) {
    if (dateString) return format(new Date(dateString), "MMMM do @ HH:mm");
    else return "";
  }

  function formatSimpleDate(dateString) {
    if (dateString) return format(new Date(dateString), "MMMM do");
    else return "";
  }

  const isUserSuspended = (user) => !!user?.suspension?.isSuspended;

  const getSuspensionShotMessage = (user) => {
    const endDate = user?.suspension?.end;
    if (endDate) {
      return `Shots disabled until your suspension ends on ${formatSimpleDate(endDate)}.`;
    }
    return "Shots disabled while your account is suspended.";
  };

  const hasUserSentShot = (shots) => {
    return shots.some((shot) => shot.user === contextUser._id);
  };

  const getShotCountClassLocal = getShotCountClass;

  const sortWinners = (candidates, spots) =>
    resolveOpenCallWinners(candidates, spots);

  const toggleDropdown = (opencallId, e) => {
    e.stopPropagation(); // Prevent the click from bubbling up and triggering document click
    setDropdownVisibility(prevState => ({
      ...prevState,
      [opencallId]: !prevState[opencallId] // Toggle visibility for the specific opencall
    }));
  };

  const handleClosedPageChange = async (nextPage) => {
    if (closedSectionLoading) return;
    if (nextPage < 1 || (closedTotalPages > 0 && nextPage > closedTotalPages)) {
      return;
    }

    setClosedSectionLoading(true);
    try {
      const items = await fetchClosedOpenCallsPage(nextPage, undefined, { expand: true });
      await syncUsersForOpenCalls([
        ...ongoingOpencalls,
        ...votingOpencalls,
        ...(items || []),
      ]);
    } catch (pageError) {
      console.error("Failed to load closed open calls page:", pageError);
    } finally {
      setClosedSectionLoading(false);
    }
  };

  const visibleClosedOpencalls =
    closedPage === 1 && !closedPreviewExpanded
      ? closedPageItems.slice(0, CLOSED_PREVIEW_COUNT)
      : closedPageItems;

  const renderClosedSectionFooter = () => {
    if (!closedSectionLoading && closedPageItems.length === 0) {
      return null;
    }

    const hasMoreToReveal =
      closedPageItems.length > CLOSED_PREVIEW_COUNT || closedTotalPages > 1;
    const canShowMore =
      !closedSectionLoading && !closedPreviewExpanded && hasMoreToReveal;
    const showPagination = closedPreviewExpanded && closedTotalPages > 1;

    if (!canShowMore && !showPagination) {
      return null;
    }

    return (
      <div className="opencall-closed-footer">
        {canShowMore && (
          <button
            type="button"
            className="opencall-show-more"
            onClick={() => setClosedPreviewExpanded(true)}
          >
            Show more
          </button>
        )}
        {showPagination && (
          <div className="pagination-controls opencall-closed-pagination">
            <button
              type="button"
              className="button"
              disabled={closedSectionLoading || closedPage <= 1}
              onClick={() => handleClosedPageChange(closedPage - 1)}
            >
              Previous
            </button>
            <span className="opencall-closed-page-label">
              Page {closedPage} of {closedTotalPages}
            </span>
            <button
              type="button"
              className="button"
              disabled={closedSectionLoading || closedPage >= closedTotalPages}
              onClick={() => handleClosedPageChange(closedPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  // Small overlapping avatar cluster - previews who sent a shot (ongoing
  // open calls) or who won (past open calls) without opening the card.
  const renderAvatarPreview = (userIds) => {
    const maxAvatars = 3;
    const visibleIds = userIds.slice(0, maxAvatars);
    const extraCount = userIds.length - visibleIds.length;

    return (
      <div className="opencall-avatar-preview">
        {visibleIds.map((id, index) => (
          <Avatar
            key={id ? String(id) : index}
            user={getUserById(id)}
            size={32}
            className="opencall-avatar-preview-item"
          />
        ))}
        {extraCount > 0 && (
          <div className="opencall-avatar-preview-more">+{extraCount}</div>
        )}
      </div>
    );
  };

  const renderOpenCallList = (
    openCalls,
    showActiveOpenCalls,
    showForVotingOpenCalls,
    showPastOpenCalls,
    sectionLoading = false,
  ) => {
    var subtitle;
    var icon;
    var color;
    var background;

    if (showActiveOpenCalls) {
      icon = "streamline:announcement-megaphone";
      subtitle = "On going Open Calls";
      color = "#f47b20";
      background = "rgba(244, 123, 32, 0.15)";
    } else if (showForVotingOpenCalls) {
      icon = "mdi:vote";
      subtitle = "Open Calls for Voting";
      color = "#00aeef";
      background = "rgba(0, 174, 239, 0.15)";
    } else if (showPastOpenCalls) {
      icon = "mdi:archive";
      subtitle = "Closed Open Calls";
      color = "#6b7280";
      background = "rgba(107, 114, 128, 0.15)";
    }

    return (
      <>
        <h2 className="opencall-subtitle">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background, color }}
            >
              <Icon icon={icon} className="icon" />
            </span>
          </span>
          {subtitle}
        </h2>

        {sectionLoading ? (
          <LoadingSpinner
            message="Loading closed open calls"
            size="sm"
            className="opencall-closed-loading"
          />
        ) : (
          <ul className="opencall-list">
            {openCalls.length === 0 && (
              <div className="no-opencalls-container">
                <p>No open calls available</p>
                <Icon
                  icon="ph:empty-light"
                  className="icon"
                  width="32"
                  height="32"
                />
              </div>
            )}
            {openCalls.map((opencall) =>
              renderOpenCallItem(
                opencall,
                showActiveOpenCalls,
                showForVotingOpenCalls,
                showPastOpenCalls
              )
            )}
          </ul>
        )}
      </>
    );
  };

  const renderOpenCallItem = (
    opencall,
    showActiveOpenCalls,
    showForVotingOpenCalls,
    showPastOpenCalls
  ) => {
    const totalSpots = opencall.numberSpots;
    const shots = Array.isArray(opencall.shots) ? opencall.shots : [];
    const winners = Array.isArray(opencall.winners) ? opencall.winners : [];
    const shotsCount = shots.length;
    const shotCountClass = getShotCountClassLocal(shotsCount, totalSpots);
    const openCallSpread = opencall.openCallSpread;
    const voterCount = getOpenCallVoteCount(opencall);
    const showVoteCount =
      !openCallSpread &&
      (showForVotingOpenCalls || showPastOpenCalls) &&
      canVoteOpenCall(contextUser);

    // Who to preview in the avatar cluster for this card - shot senders on
    // ongoing open calls, winners on past ones.
    const avatarPreviewIds = !openCallSpread
      ? showActiveOpenCalls
        ? shots.map((shot) => shot.user)
        : showPastOpenCalls
          ? winners
          : []
      : [];
    const hasAvatarPreview = avatarPreviewIds.length > 0;

    return (
      <li
        key={opencall._id}
        className="opencall-item"
        onClick={() => toggleEventInfo(opencall._id)}
      >
        <div className="opencall-header">
          <div className="opencall-info">
            <div className="opencall-name">
              {formatSimpleDate(events[normalizeOpenCallId(opencall._id)]?.date)} ∙{" "}
              {opencall.openCallName}
            </div>
            <div className="opencall-preview-row">
              <div className="opencall-preview-pills">
                {!openCallSpread && (
                  <div className={`opencall-spots-pill ${shotCountClass}`}>
                    <Icon
                      icon={shotCountClass === "shots-high" ? "mdi:check-circle" : "mdi:clock-outline"}
                      className="icon"
                    />
                    <span>{shotsCount} / {totalSpots} spots filled</span>
                  </div>
                )}
                {showVoteCount && (
                  <div className="opencall-votes-pill">
                    <Icon icon="mdi:vote" className="icon" />
                    <span>
                      {voterCount} voters
                    </span>
                  </div>
                )}
                {openCallSpread && (
                  <span className="opencall-spreadsheet-label">
                    <b>Spreadsheet</b>
                  </span>
                )}
              </div>
              {hasAvatarPreview && renderAvatarPreview(avatarPreviewIds)}
            </div>
            {showForVotingOpenCalls && canVoteOpenCall(contextUser) && (
              <div className="voting-deadline">
                {opencall.votingDeadline == null ? (
                  <>Vote <b> ASAP </b></>
                ) : (
                  <>
                    Voting until <b>{formatDate(opencall.votingDeadline)}</b>
                  </>
                )}
              </div>
            )}
            {showActiveOpenCalls && !openCallSpread && (
              <p style={{ marginBottom: "0" }}>
                Open until <b>{formatDate(opencall.openCallDeadline)}</b>
              </p>
            )}
            {showForVotingOpenCalls &&
              openCallUserHasVoted(opencall, contextUser._id) && (
                <p
                  className="opencall-voters"
                  style={{ marginBottom: "0", fontStyle: "italic" }}
                >
                  You already voted for this Open Call
                </p>
              )}
          </div>

          {/* On going Open Calls */}
          {showActiveOpenCalls && (
            <div className="opencall-actions">
              {!openCallSpread ? (
                hasUserSentShot(shots) ? (
                  <button
                    className="cancel-shot-btn"
                    onClick={(e) => handleCancelShot(opencall._id, e)}
                  >
                    <Icon icon="mdi:cancel" className="icon" />
                    Cancel Shot
                  </button>
                ) : isUserSuspended(contextUser) ? (
                  <p className="opencall-suspended-message" onClick={(e) => e.stopPropagation()}>
                    <Icon icon="mdi:account-cancel-outline" className="icon" />
                    {getSuspensionShotMessage(contextUser)}
                  </p>
                ) : (
                  <button
                    className="send-shot-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCommentInputFor(opencall._id);
                      setShotComment("");
                    }}
                  >
                    <Icon icon="nimbus:shot" className="icon" />
                    Shot!
                  </button>
                )
              ) : (
                <a
                  href={openCallSpread}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="spreadsheet-btn"
                  style={{
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon icon="icon-park-outline:excel" className='icon' />
                  <span className="text">Spreadsheet</span>
                </a>
              )}
            </div>
          )}

          {/* Choosing the winners */}
          {showForVotingOpenCalls && (
            <div className="opencall-actions">
              {!canVoteOpenCall(contextUser) && !canViewOpenCallResults(contextUser) && (
                <Icon
                  icon="svg-spinners:clock"
                  className="icon"
                  width={28}
                  height={28}
                />
              )}

            {(canVoteOpenCall(contextUser) && !canViewOpenCallResults(contextUser) && !openCallUserHasVoted(opencall, contextUser._id)) &&
              <button
                className='vote-btn'
                onClick={(e) => handleVoting(opencall, e)}
              >
                <Icon icon="mdi:vote" className='icon' />
              </button>
            }

            {canViewOpenCallResults(contextUser) &&
              <div className="dropdown-container">
                {/* Three dots button */}
                <button className="menu-toggle-btn" onClick={(e) => toggleDropdown(opencall._id, e)}>
                  <Icon icon="iconamoon:options-duotone" className="icon" />
                </button>

                {/* Actual Dropdown */}
                {dropdownVisibility[opencall._id] && (<div className='buttons-dropdown-menu'>
                  {(!openCallUserHasVoted(opencall, contextUser._id)) && (<button className='buttons-dropdown-item'
                    onClick={(e) => handleVoting(opencall, e)}
                  >
                    <Icon icon="mdi:vote" className='icon' /> Vote
                  </button>)}
                  <button className='buttons-dropdown-item'
                    onClick={(e) => initiateCloseOpenCall(opencall, e)}
                  >
                    <Icon icon="mdi:book-lock" className='icon' /> Close Voting
                  </button>
                  <button className='buttons-dropdown-item'
                    onClick={(e) => handleSeeVoting(opencall, e)}
                  >
                    <Icon icon="mdi:eye" className='icon' /> Check Voting
                  </button>
                </div>)}
              </div>}

            {/*{(contextUser.access_level >= 0 || contextUser.position === "Events Manager" || contextUser.position === "Head of IT") &&
              <button
                className='close-voting-btn'
                onClick={(e) => handleCloseOpenCall(opencall._id, opencall.event, sortWinners(opencall.votes, opencall.numberSpots), e)}
              >
                <Icon icon="wpf:inspection" className='icon' />
              </button>}

            {(contextUser.access_level >= 0 || contextUser.position === "Events Manager" || contextUser.position === "Head of IT") &&
              <button
                className='see-voting-btn'
                onClick={(e) => handleSeeVoting(opencall, e)}
              >
                <Icon icon="mdi:eye" className='icon' />
              </button>
            }*/}
          </div>
          )}

          {/* Past Open Calls - Eye button for viewing results */}
          {showPastOpenCalls && !openCallSpread && (
            <div className="opencall-actions">
              {canViewOpenCallResults(contextUser) && (
                <>
                  <button
                    className="see-voting-btn"
                    onClick={(e) => handleSeeVoting(opencall, e)}
                  >
                    <Icon icon="mdi:eye" className="icon" />
                  </button>

                  <button
                    className="reopen-voting-btn"
                    onClick={(e) => handleReopenForVoting(opencall, e)}
                    title="Reopen for voting"
                  >
                    <Icon icon="mdi:refresh" className="icon" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* More information when selected code */}
        {
          selectedEventId === opencall._id && (
            <div className='opencall-details'>
              {!openCallSpread && <div className="opencall-shots-info">
                <div className="opencall-shot-title">
                  <Icon icon="nimbus:shot" className="opencall-section-icon" /><p>Shots: </p>
                </div>
                {shots.length > 0 ?
                  <div>{shots.map(shot =>
                      (
                        <Link
                          key={shot}
                          className="user-link"
                          to={`/users/${shot.user}`}
                        >
                          <Avatar user={getUserById(shot.user)} />
                          <span className="user-link-text">{getUserNameById(shot.user)}</span>
                        </Link>
                  ))}</div>
                  : <div>No shot received</div>}
              </div>}
              <div className="opencall-description">
                <div>
                  <Icon icon="mdi:text" className="opencall-section-icon" />
                  <p style={{ marginBottom: '0' }}><b>Description</b></p>
                </div>
                <div className="comment">
                  {opencall.comment}
                </div>
              </div>
              {showPastOpenCalls && openCallSpread && (
                <div className="opencall-shots-info">
                  <div className="opencall-shot-title">
                    <Icon icon="garden:file-spreadsheet-fill-12" className="opencall-section-icon" />
                    <p>Spreadsheet: </p>
                  </div>
                  <a
                    href={openCallSpread}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 600, textDecoration: "underline" }}
                  >
                    Open Spreadsheet
                  </a>
                </div>
              )}
              {showPastOpenCalls && !opencall.openCallSpread && (<div className="opencall-shots-info">
                <div className="opencall-shot-title">
                  <Icon icon="mdi:prize" className="opencall-section-icon" /><p>Winners: </p>
                </div>
                {winners.length > 0 ?
                  <div>{winners.map(userId => {
                      const shotComment = shots.find(
                        (shot) => String(shot.user) === String(userId)
                      )?.comment;
                      return (
                        <Link
                          key={userId}
                          className="user-link"
                          to={`/users/${userId}`}
                        >
                          <Avatar user={getUserById(userId)} />
                          <span className="user-link-text">
                            {getUserNameById(userId)}
                            {canViewOpenCallResults(contextUser) && shotComment && (
                              <span className="winner-shift-comment"> — {shotComment}</span>
                            )}
                          </span>
                        </Link>
                      );
                  })}</div>
                  : <div>There are no winners</div>}
              </div>)}

            </div>
          )
        }
      </li >
    );
  };

  const openCallContent = (
    <>
      <div className="opencall-container">
        {showCommentInputFor && (
          <div
            className="shot-comment-overlay"
            onClick={() => {
              setShowCommentInputFor(null);
              setShotComment("");
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="shot-comment-container"
            >
              <h3>Add a comment to your shot!</h3>
              <input
                type="text"
                placeholder="Your comment..."
                value={shotComment}
                onChange={(e) => setShotComment(e.target.value)}
                style={{ width: "100%", marginBottom: 12, padding: 8 }}
              />
              <div
                style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
              >
                <button
                  className="send-shot-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendShot(showCommentInputFor, e, shotComment);
                  }}
                >
                  Shot!
                </button>
                <button
                  className="cancel-shot-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCommentInputFor(null);
                    setShotComment("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {renderOpenCallList(ongoingOpencalls, true, false, false)}

        {renderOpenCallList(votingOpencalls, false, true, false)}

        {renderOpenCallList(visibleClosedOpencalls, false, false, true, closedSectionLoading)}
        {renderClosedSectionFooter()}
      </div>

      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss={true}
          timeout={4000}
          onClose={() => setAlertInfo({ show: false, type: "", message: "" })}
        />
      )}

      {tieBreakerInfo.show && (
        <OpenCallTieBreaker
          tieGroups={tieBreakerInfo.tieGroups}
          automaticWinners={tieBreakerInfo.automaticWinners}
          selections={tieBreakerInfo.selections}
          onSelectionChange={handleTieBreakerSelectionChange}
          getUserById={getUserById}
          getUserNameById={getUserNameById}
          getShotComment={getShotCommentByUserId}
          validationError={tieBreakerInfo.validationError}
          onCancel={() =>
            setTieBreakerInfo({
              show: false,
              openCallId: null,
              eventId: null,
              votes: null,
              spots: 0,
              automaticWinners: [],
              tieGroups: [],
              selections: {},
              validationError: "",
              shots: [],
            })
          }
          onConfirm={handleTieBreakerConfirm}
        />
      )}

      {alertActionInfo.show && (
        <CustomAlertWithButtons
          title={alertActionInfo.title}
          type={alertActionInfo.type}
          message={alertActionInfo.message}
          onButton1Click={alertActionInfo.btn1Action}
          onButton2Click={alertActionInfo.btn2Action}
          button1Text={alertActionInfo.btn1Text}
          button2Text={alertActionInfo.btn2Text}
          showCheckbox={alertActionInfo.showCheckbox}
          checkboxLabel={alertActionInfo.checkboxLabel}
          checkboxValue={sendEmailNotification}
          onCheckboxChange={(value) => {
            setSendEmailNotification(value);
          }}
        />
      )}
    </>
  );

  if (loading && embedded) {
    return <LoadingSpinner message="Loading open calls" size="sm" />;
  }

  if (embedded) {
    return openCallContent;
  }

  return (
    <div className="page-container">
      <NavHeader />

      <div className="page-title-row">
        <h1 className="page-title page-title-icon">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: "rgba(236, 0, 140, 0.15)", color: "#ec008c" }}
            >
              <Icon icon="bxs:megaphone" className="icon" />
            </span>
          </span>
          Open Calls
        </h1>
      </div>

      {openCallContent}
    </div>
  );
}

export default OpenCallList;
